import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Chart,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
} from "chart.js/auto";
import { Line } from "react-chartjs-2";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";

Chart.register(LineElement, CategoryScale, LinearScale, PointElement);

const CREAM = "#f5e8c7";
// Colors assigned to compared friends, in the order they are picked
const FRIEND_COLORS = ["#5eead4", "#fb923c", "#c084fc"];
const MAX_COMPARE = 3;
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const RANGES = [
  { id: "week", label: "1W", days: 7, sub: "last 7 days" },
  { id: "month", label: "1M", days: 30, sub: "last 30 days" },
  { id: "quarter", label: "3M", days: 90, sub: "last 3 months" },
  { id: "year", label: "1Y", days: 365, sub: "last 12 months" },
  { id: "all", label: "All", days: null, sub: "all time" },
];

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

function rowsToMap(rows) {
  const map = {};
  (rows || []).forEach((r) => {
    if (r && r.date) map[r.date] = parseInt(r.dayminutes, 10) || 0;
  });
  return map;
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

const LineChart = ({
  data,
  ownerName = "You",
  friends = [],
  canCompare = false,
}) => {
  const [rangeId, setRangeId] = useState("month");
  // Friend emails in the order they were picked (order decides their color)
  const [selected, setSelected] = useState([]);
  // email -> heatmap rows; null while the request is in flight
  const [friendSeries, setFriendSeries] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const range = RANGES.find((r) => r.id === rangeId) || RANGES[1];

  useEffect(() => {
    if (!pickerOpen) return;
    const onPointerDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [pickerOpen]);

  // Fetch stats for newly selected friends, results are cached for the session
  useEffect(() => {
    selected.forEach((email) => {
      if (friendSeries[email] !== undefined) return;
      setFriendSeries((prev) => ({ ...prev, [email]: null }));
      (async () => {
        try {
          const response = await fetch(
            "https://animedoro-backend.vercel.app/api/getstats",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email,
                curDate: new Date().toDateString(),
              }),
            }
          );
          const json = await response.json();
          setFriendSeries((prev) => ({
            ...prev,
            [email]: response.status === 200 ? json.heatmapdays || [] : [],
          }));
        } catch (e) {
          setFriendSeries((prev) => ({ ...prev, [email]: [] }));
        }
      })();
    });
  }, [selected, friendSeries]);

  const toggleFriend = (email) => {
    setSelected((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : prev.length >= MAX_COMPARE
        ? prev
        : [...prev, email]
    );
  };

  const people = useMemo(() => {
    const list = [
      {
        key: "own",
        name: capitalize(ownerName),
        rows: data || [],
        color: CREAM,
        isOwn: true,
        loading: false,
      },
    ];
    selected.forEach((email, i) => {
      const f = friends.find((fr) => fr.email === email);
      list.push({
        key: email,
        name: capitalize((f && f.name) || email.split("@")[0]),
        rows: friendSeries[email] || [],
        loading: friendSeries[email] == null,
        color: FRIEND_COLORS[i % FRIEND_COLORS.length],
        isOwn: false,
      });
    });
    return list;
  }, [data, ownerName, selected, friends, friendSeries]);

  // Continuous day-by-day axis ending today, so every person's line aligns
  const labels = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let days = range.days;
    if (!days) {
      let earliest = null;
      people.forEach((p) => {
        p.rows.forEach((r) => {
          if (r && r.date && (!earliest || r.date < earliest)) {
            earliest = r.date;
          }
        });
      });
      if (earliest) {
        const [y, m, d] = earliest.split("-").map(Number);
        const start = new Date(y, m - 1, d);
        days = Math.floor((today - start) / 864e5) + 1;
      }
      days = Math.max(days || 0, 30);
    }
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      out.push(
        dateKey(
          new Date(today.getFullYear(), today.getMonth(), today.getDate() - i)
        )
      );
    }
    return out;
  }, [range, people]);

  const showPoints = labels.length <= 31;
  const longRange = labels.length > 200;
  const comparing = selected.length > 0;

  const chartData = {
    labels,
    datasets: people.map((p) => {
      const map = rowsToMap(p.rows);
      return {
        label: p.name,
        data: labels.map((l) => (map[l] !== undefined ? map[l] : 0)),
        borderColor: p.color,
        borderWidth: 2,
        tension: 0.35,
        pointRadius: showPoints ? 2 : 0,
        pointBackgroundColor: p.color,
        pointHitRadius: 12,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: p.color,
        pointHoverBorderColor: `${p.color}66`,
        pointHoverBorderWidth: 4,
        fill: p.isOwn,
        backgroundColor: p.isOwn
          ? (context) => {
              const { ctx, chartArea } = context.chart;
              if (!chartArea) return "rgba(245, 232, 199, 0.08)";
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.top,
                0,
                chartArea.bottom
              );
              gradient.addColorStop(0, "rgba(245, 232, 199, 0.30)");
              gradient.addColorStop(1, "rgba(245, 232, 199, 0)");
              return gradient;
            }
          : "transparent",
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#1d1816",
        titleColor: CREAM,
        bodyColor: "#cdc1a5",
        borderColor: "rgba(245, 232, 199, 0.25)",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 10,
        displayColors: comparing,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        callbacks: {
          title: (items) => {
            if (!items.length) return "";
            const [y, m, d] = String(items[0].label).split("-");
            return `${MONTH_NAMES[+m - 1]} ${+d}, ${y}`;
          },
          label: (item) =>
            comparing
              ? ` ${item.dataset.label}: ${item.parsed.y} min`
              : ` ${item.parsed.y} minutes focused`,
        },
      },
    },
    scales: {
      x: {
        type: "category",
        grid: { display: false },
        ticks: {
          color: "#9b9183",
          maxTicksLimit: longRange ? 8 : 6,
          maxRotation: 0,
          autoSkip: true,
          font: { size: 10 },
          callback: function (value, index) {
            const key = labels[index];
            if (!key) return "";
            const [y, m, d] = key.split("-");
            return longRange
              ? `${MONTH_NAMES[+m - 1]} '${y.slice(2)}`
              : `${MONTH_NAMES[+m - 1]} ${+d}`;
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(245, 232, 199, 0.06)",
        },
        ticks: {
          color: "#9b9183",
          maxTicksLimit: 5,
          font: { size: 10 },
        },
      },
    },
  };

  return (
    <div className="chart-card">
      <div className="card-head">
        <div>
          <h2 className="section-title">Focus Trend</h2>
          <p className="section-sub">Daily focus minutes · {range.sub}</p>
        </div>
        <div className="chart-controls">
          <div className="range-switch">
            {RANGES.map((r) => (
              <button
                key={r.id}
                className={rangeId === r.id ? "active" : ""}
                onClick={() => setRangeId(r.id)}
              >
                {r.label}
              </button>
            ))}
          </div>
          {canCompare && (
            <div className="compare-wrap" ref={pickerRef}>
              <button
                className={`compare-btn ${comparing ? "active" : ""}`}
                onClick={() => setPickerOpen((o) => !o)}
              >
                <GroupAddOutlinedIcon style={{ fontSize: 16 }} />
                Compare
                {comparing && (
                  <span className="compare-count">
                    {selected.length}/{MAX_COMPARE}
                  </span>
                )}
              </button>
              {pickerOpen && (
                <div className="compare-menu">
                  {friends.length === 0 ? (
                    <p className="compare-empty">
                      Add friends to compare your focus trends with theirs.
                    </p>
                  ) : (
                    <>
                      <p className="compare-hint">
                        Pick up to {MAX_COMPARE} friends
                      </p>
                      {friends.map((f) => {
                        const idx = selected.indexOf(f.email);
                        const checked = idx !== -1;
                        const full =
                          !checked && selected.length >= MAX_COMPARE;
                        return (
                          <button
                            key={f.email}
                            className={`compare-option ${
                              full ? "disabled" : ""
                            }`}
                            disabled={full}
                            onClick={() => toggleFriend(f.email)}
                          >
                            <span
                              className="compare-check"
                              style={
                                checked
                                  ? {
                                      backgroundColor:
                                        FRIEND_COLORS[
                                          idx % FRIEND_COLORS.length
                                        ],
                                      borderColor:
                                        FRIEND_COLORS[
                                          idx % FRIEND_COLORS.length
                                        ],
                                    }
                                  : {}
                              }
                            >
                              {checked ? "✓" : ""}
                            </span>
                            <span className="compare-name">
                              {capitalize(f.name)}
                            </span>
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {comparing && (
        <div className="chart-legend-row">
          {people.map((p) => (
            <span
              key={p.key}
              className="chart-legend-chip"
              style={{ borderColor: `${p.color}55` }}
            >
              <span
                className="chart-legend-dot"
                style={{ backgroundColor: p.color }}
              ></span>
              {p.name}
              {p.loading && <span className="chart-legend-loading">…</span>}
              {!p.isOwn && (
                <button
                  className="chart-legend-remove"
                  onClick={() => toggleFriend(p.key)}
                  aria-label={`Remove ${p.name}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <div className="chart-wrap">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default LineChart;
